import { useEffect, useState, useCallback, useRef } from "react";
import { IResourceData } from "../types/resource";
import { getResourcePaginatedList } from "../services/resourcesService";
import { IPaginatedProps } from "../types";

interface IUseFetchResources extends IPaginatedProps {
  searchTerm?: string;
  system_id?: string | number;
  isActive?: string;
}

const useFetchPaginatedResources = ({
  currentDomainId,
  setIsLoading,
  page,
  pSize,
  searchTerm,
  system_id,
  isActive = "true",
}: IUseFetchResources) => {
  const [resourcesData, setResourcesData] = useState<IResourceData[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result: IResourceData[] = await getResourcePaginatedList({
          currentDomainId,
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          system_id,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setResourcesData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setResourcesData([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentDomainId,
      page,
      pSize,
      searchTerm,
      system_id,
      isActive,
      setIsLoading,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [resourcesData, fetchData];
};

export default useFetchPaginatedResources;
